import { createContext, useContext, useMemo, useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../store/authSlice';
import { useSocket, disconnectSocket } from '../hooks/useSocket';

/**
 * SocketContext — provides socket state and methods to the entire app tree
 *
 * Usage:
 *   <SocketProvider>
 *     <App />
 *   </SocketProvider>
 *
 *   // In any child component:
 *   const { connected, onlineUsers } = useSocketContext();
 */
const SocketContext = createContext({
  connected: false,
  onlineUsers: [],
  typingUsers: {},
});

export const SocketProvider = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { connected, on, off, emit, setPresence } = useSocket();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});

  // ── Connect/disconnect based on auth state ──
  useEffect(() => {
    if (isAuthenticated) {
      // Socket auto-connects via useSocket
      setPresence('online');

      // Handle page visibility
      const handleVisibility = () => {
        setPresence(document.hidden ? 'away' : 'online');
      };
      document.addEventListener('visibilitychange', handleVisibility);

      // Handle before unload
      const handleUnload = () => {
        setPresence('offline');
      };
      window.addEventListener('beforeunload', handleUnload);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('beforeunload', handleUnload);
      };
    } else {
      disconnectSocket();
    }
  }, [isAuthenticated, setPresence]);

  // ── Presence list ──
  useEffect(() => {
    if (!connected) return;

    const handlePresenceList = (users) => {
      setOnlineUsers(users || []);
    };

    const handlePresenceUpdate = ({ userId, status }) => {
      setOnlineUsers((prev) => {
        if (status === 'offline') return prev.filter((id) => id !== userId);
        if (!prev.includes(userId)) return [...prev, userId];
        return prev;
      });
    };

    const cleanup1 = on('presence:list', handlePresenceList);
    const cleanup2 = on('hivelink:presence:update', handlePresenceUpdate);

    // Request initial presence list
    emit('presence:list');

    return () => {
      cleanup1?.();
      cleanup2?.();
    };
  }, [connected, on, emit]);

  // ── Typing indicators ──
  useEffect(() => {
    if (!connected) return;

    const handleTypingStart = ({ channelId, userId }) => {
      setTypingUsers((prev) => ({
        ...prev,
        [channelId]: [...new Set([...(prev[channelId] || []), userId])],
      }));
    };

    const handleTypingStop = ({ channelId, userId }) => {
      setTypingUsers((prev) => ({
        ...prev,
        [channelId]: (prev[channelId] || []).filter((id) => id !== userId),
      }));
    };

    const cleanup1 = on('hivelink:typing:start', handleTypingStart);
    const cleanup2 = on('hivelink:typing:stop', handleTypingStop);

    return () => {
      cleanup1?.();
      cleanup2?.();
    };
  }, [connected, on]);

  const value = useMemo(() => ({
    connected,
    onlineUsers,
    typingUsers,
  }), [connected, onlineUsers, typingUsers]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => useContext(SocketContext);

export default SocketProvider;
